/* Accounts, as the rest of the app sees them.
 *
 * Everything supabase-js knows about auth stops here. The UI asks this module
 * for a user and gets a plain object; it calls signIn and gets `{ ok, error }`,
 * the same shape `addCard` uses for a refusal. Nothing above this file imports
 * the client, which is what keeps swapping the provider a one-file job.
 *
 * Signing in is additive. Everything the app does — building, playtesting, the
 * meta — works signed out, exactly as it did before there were accounts, and
 * every failure in here is survivable for that reason. See #48 for what an
 * account is eventually *for*.
 */
import { supabase, hasClient, SUPABASE_URL, SUPABASE_PUBLISHABLE_KEY } from './supabase.js';

let session = null;
let settings = null;
const listeners = new Set();

/**
 * A DOM shim is not a browser.
 *
 * `test/app.test.mjs` renders the whole app under Node against a hand-written
 * `document` and `window`, which is enough for supabase-js to conclude it is in
 * a browser and open a cross-tab `BroadcastChannel` plus a token-refresh
 * interval. Those outlive the suite: every assertion passes and the process
 * never exits, which surfaces as a hung CI job rather than a failing one. It is
 * also version-dependent — Node 22 tolerated `dispose()`, Node 20 did not —
 * so the guard is on the environment rather than on cleaning up afterwards.
 *
 * Nothing outside a browser has a session to restore, so this costs nothing.
 * `process.versions.node` is the discriminator because bundlers sometimes
 * define a `process` object in browser output, but never that.
 */
const IN_BROWSER = typeof process === 'undefined' || !process.versions?.node;

export const currentUser = () => session?.user ?? null;
export const isSignedIn = () => !!session;

/* ---------------------------------------------------------------------- */
/* the Discord token                                                       */
/* ---------------------------------------------------------------------- */

/**
 * Where the Discord OAuth token is kept, and why it is kept at all.
 *
 * The supporter API (#96) authenticates with the user's own Discord token: the
 * server asks Discord whether the holder has the supporter role, which is what
 * makes entitlement a role rather than a purchase record. So the client has to
 * be able to hand that token over.
 *
 * supabase-js emits `provider_token` **once**, on the session immediately
 * after the OAuth redirect, and drops it on the next token refresh — about an
 * hour later. Reading it off the live session therefore works for exactly one
 * hour after signing in and then silently stops, which would land somebody who
 * is paying in "it says I'm not a member" for reasons invisible to them. That
 * is the failure `src/entitlement.js` is arranged to avoid, so it should not
 * be reintroduced here.
 *
 * Keeping a copy is Supabase's own documented answer to this — their
 * `onAuthStateChange` example stores `provider_token` in local storage and
 * says the value is emitted only once. It is the same value already sitting in
 * the same storage under the session, so this is not new exposure, and the
 * page's Content-Security-Policy has no `'unsafe-inline'` for anything to read
 * it with.
 *
 * The proper fix is a short-lived entitlement token issued by our own server
 * at sign-in, so the Discord credential never has to be kept at all — #112.
 */
const DISCORD_TOKEN_KEY = 'optcg.discord.token';

const store = () => { try { return globalThis.localStorage ?? null; } catch { return null; } };

/** Keep whatever a session is carrying; never overwrite a good one with null. */
function rememberDiscordToken(next) {
  const token = next?.provider_token;
  if (!token) return;
  try { store()?.setItem(DISCORD_TOKEN_KEY, token); } catch { /* private mode, or full */ }
}

function forgetDiscordToken() {
  try { store()?.removeItem(DISCORD_TOKEN_KEY); } catch { /* nothing to do */ }
}

/**
 * The Discord token for this session, or null.
 *
 * Null is not "not a supporter" — it is "we cannot ask", which the API turns
 * into an invitation to sign in rather than a refusal.
 */
export function discordToken() {
  if (!session) return null;
  /* The live session first. It carries `provider_token` for the first hour
   * after signing in, which covers two cases the stored copy does not: a
   * browser where storage is unavailable or full, and a session that signed in
   * before this code existed. Only once it has been dropped does the copy
   * matter, and by then it is the same value. */
  if (session.provider_token) return session.provider_token;
  try { return store()?.getItem(DISCORD_TOKEN_KEY) || null; } catch { return null; }
}

/** A short label for the header: whatever we actually know about them. */
export function userLabel() {
  const u = currentUser();
  if (!u) return null;
  return u.user_metadata?.full_name || u.email || 'Signed in';
}

/** Subscribe to sign-in and sign-out. Returns an unsubscribe. */
export function onAuthChange(fn) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

const emit = () => { for (const fn of listeners) fn(currentUser()); };

/**
 * Restore a session and start tracking changes. Call once, at boot.
 *
 * `getSession` reads storage rather than the network, so a signed-in reload
 * paints signed-in without waiting on Supabase. It still returns a promise, and
 * it can still reject — a corrupt stored session, a blocked third-party
 * storage partition — so the caller gets `null` rather than an exception. A
 * broken session must cost you the account, not the app.
 */
export async function initAuth() {
  if (!IN_BROWSER) return null;

  /* Warm the settings cache while the card database is still loading. By the
   * time anyone reaches for the Sign in button the answer is already here, so
   * the dialog can be built once rather than rebuilt underneath whoever is
   * typing into it. Deliberately not awaited and deliberately swallowed: this
   * is an optimisation, and `authSettings` falls back on its own. */
  authSettings().catch(() => {});

  try {
    const { data } = await supabase().auth.getSession();
    session = data?.session ?? null;
    rememberDiscordToken(session);
    supabase().auth.onAuthStateChange((event, next) => {
      session = next ?? null;
      /* The one moment `provider_token` exists. Registering this callback
       * immediately after the client is created is Supabase's own instruction
       * for the same reason: the value is emitted once and never again. */
      if (event === 'SIGNED_OUT' || !next) forgetDiscordToken();
      else rememberDiscordToken(next);
      emit();
    });
  } catch (err) {
    console.warn(`Auth unavailable: ${err.message}`);
    session = null;
  }
  return currentUser();
}

/**
 * Undo `initAuth`. Nothing in the browser calls this — a page that is going
 * away takes its background work with it.
 *
 * `IN_BROWSER` above means the boot-path test never gets far enough to need
 * this, and it stays because the guard only covers the path that starts a
 * client by itself. Any test that deliberately signs in has to hand the
 * client's background work back, and `dispose()` is auth-js's own teardown for
 * that — `stopAutoRefresh()` leaves the cross-tab channel open.
 */
export async function stopAuth() {
  listeners.clear();
  session = null;
  if (hasClient()) await supabase().auth.dispose();
}

/**
 * What this project's auth is actually configured to allow, asked of the
 * server rather than hardcoded.
 *
 * Enabling Google is a dashboard action, not a deploy — so a constant in here
 * would be a second place to remember, and the failure mode is a sign-in button
 * that does nothing. `/auth/v1/settings` is public and says which providers are
 * live, whether signups are open, and whether a new account has to confirm its
 * email before it can sign in.
 */
export async function authSettings() {
  if (settings) return settings;
  const fallback = { google: false, discord: false, email: true, signupsOpen: true, confirmEmail: true };
  try {
    const r = await fetch(`${SUPABASE_URL}/auth/v1/settings`, {
      headers: { apikey: SUPABASE_PUBLISHABLE_KEY },
    });
    if (!r.ok) return fallback;
    const j = await r.json();
    settings = {
      google: !!j.external?.google,
      discord: !!j.external?.discord,
      email: !!j.external?.email,
      signupsOpen: !j.disable_signup,
      confirmEmail: !j.mailer_autoconfirm,
    };
    return settings;
  } catch {
    /* Not cached: a network blip should not pin the UI to the fallback for the
     * rest of the session. */
    return fallback;
  }
}

/* ---------------------------------------------------------------------- */
/* the four things you can do                                              */
/* ---------------------------------------------------------------------- */

/* Supabase's own messages are good ("Invalid login credentials", "User already
 * registered"), so they are shown as-is. These two are the exceptions: one is
 * jargon, and the other is the single most likely failure while the project is
 * still being set up, where the real message says nothing useful. */
const FRIENDLIER = {
  'Email not confirmed': 'That account still needs confirming — check your email for the link.',
  'Failed to fetch': 'Could not reach the account service. Check your connection.',
};

const failed = err => ({
  ok: false,
  error: FRIENDLIER[err?.message] || err?.message || 'Something went wrong.',
});

export async function signIn(email, password) {
  try {
    const { error } = await supabase().auth.signInWithPassword({ email, password });
    return error ? failed(error) : { ok: true };
  } catch (err) { return failed(err); }
}

/**
 * `needsConfirmation` is the difference between "you are in" and "go and check
 * your email", and the UI has to say which — a sign-up that silently does
 * nothing visible is indistinguishable from one that failed.
 */
export async function signUp(email, password) {
  try {
    const { data, error } = await supabase().auth.signUp({ email, password });
    if (error) return failed(error);
    return { ok: true, needsConfirmation: !data?.session };
  } catch (err) { return failed(err); }
}

/**
 * Extra OAuth scopes, per provider.
 *
 * Discord's default scopes identify the person but say nothing about which
 * servers they are in. `guilds.members.read` is what lets us ask "does this
 * account hold the supporter role in our guild" — which is the entitlement
 * check the whole supporter tier rests on (#40), so it has to be requested at
 * sign-in rather than bolted on later.
 *
 * Deliberately not `guilds`: that returns every server the user is in, which
 * is far more than we need and more than anyone should hand over to a deck
 * builder. `guilds.members.read` is scoped to a guild we name.
 */
const SCOPES = {
  discord: 'identify email guilds.members.read',
};

/**
 * Hands the whole page to an OAuth provider and comes back to where it started.
 *
 * `redirectTo` has to be on Supabase's redirect allow-list (Authentication ->
 * URL Configuration) or the round trip ends on an error page rather than here.
 *
 * Which providers are live is read from the server — see `authSettings` — so
 * this never needs a list of its own.
 */
export async function signInWithProvider(provider) {
  try {
    const { error } = await supabase().auth.signInWithOAuth({
      provider,
      options: {
        redirectTo: `${window.location.origin}${window.location.pathname}`,
        ...(SCOPES[provider] ? { scopes: SCOPES[provider] } : {}),
      },
    });
    return error ? failed(error) : { ok: true };
  } catch (err) { return failed(err); }
}

export async function signOut() {
  try {
    const { error } = await supabase().auth.signOut();
    /* Before checking the error, not after. Signing out has to take the
     * Discord token with it whether or not Supabase managed to tell its
     * server — a credential left behind by a failed sign-out is the worst of
     * both outcomes. */
    forgetDiscordToken();
    return error ? failed(error) : { ok: true };
  } catch (err) { forgetDiscordToken(); return failed(err); }
}
